import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export function productUrlWithContext(productUrl, contextUrl) {
  const url = new URL(productUrl);
  if (contextUrl && new URL(contextUrl).pathname === url.pathname) {
    for (const [key, value] of new URL(contextUrl).searchParams) {
      if (key !== 'variant') url.searchParams.set(key, value);
    }
  }
  return url.toString();
}

export class QuickAddComponent extends Component {
  static #latestRequest = 0;
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get quickViewOnly() {
    return this.dataset.quickViewOnly !== undefined;
  }

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#cartUpdateAbortController = new AbortController();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState, {
      signal: this.#cartUpdateAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;
    document.getElementById('quick-add-dialog')?.cancelStitchedSelection?.();
    const request = ++QuickAddComponent.#latestRequest;
    this.toggleAttribute('data-loading', true);
    try {

      // Check if we have cached content for this URL
      let productGrid = this.quickViewOnly ? null : this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        // Fetch and cache the content
        let html;
        try {
          html = await this.fetchProductPage(currentUrl);
        } catch {
          if (request === QuickAddComponent.#latestRequest) window.location.assign(currentUrl);
          return;
        }
        if (request !== QuickAddComponent.#latestRequest || !html) return;
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            if (!this.quickViewOnly) this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (request !== QuickAddComponent.#latestRequest) return;
      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent, currentUrl);
        if (request !== QuickAddComponent.#latestRequest) return;
        this.#updateVariantPicker(productGrid);
      } else {
        window.location.assign(currentUrl);
        return;
      }

      this.#openQuickAddModal();
    } finally {
      this.toggleAttribute('data-loading', false);
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.returnFocusTarget = this.querySelector('.quick-add__button--choose');
    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;

    try {
      const url = new URL(productPageUrl);
      if (this.quickViewOnly) url.searchParams.set('_bape_stock', String(Date.now()));
      const response = await fetch(url.toString(), {
        signal: controller.signal,
        ...(this.quickViewOnly ? { cache: 'no-store' } : {}),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      if (this.#abortController === controller) this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid, currentUrl = this.productPageUrl, { title = this.dataset.productTitle, replace = false } = {}) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent) dialogComponent.dataset.productUrl = currentUrl;
    for (const link of productGrid.querySelectorAll('.view-product-title a')) link.href = currentUrl;
    const descriptionTemplate = productGrid.querySelector('[data-quick-view-description]');
    const description = descriptionTemplate?.content.firstElementChild?.cloneNode(true);
    descriptionTemplate?.remove();

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = title || '';

      // Make product title as a link to the product page
      productTitle.href = currentUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (description) productHeader.appendChild(description);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    } else if (description) {
      productGrid.querySelector('.view-product-title')?.after(description);
    }

    if (replace) modalContent.replaceChildren(...productGrid.childNodes);
    else {
      morph(modalContent, productGrid);
      this.#syncVariantSelection(modalContent);
    }
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = !this.quickViewOnly && productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

export class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #stitchedController;

  cancelStitchedSelection() {
    this.#stitchedController?.abort();
    this.#stitchedController = undefined;
  }

  /** Replace the whole product so media, price, availability and the cart form agree. */
  async selectStitchedProduct(productUrl, productId) {
    const url = new URL(productUrl, window.location.href);
    if (url.origin !== new URL(window.location.href).origin || !url.pathname.includes('/products/')) return;
    const dialog = this.refs.dialog;
    const openerButton = this.returnFocusTarget;
    const opener = openerButton?.closest('quick-add-component');
    if (!dialog?.open || !opener?.updateQuickAddModal) return;
    this.cancelStitchedSelection();
    const controller = new AbortController();
    this.#stitchedController = controller;
    const active = () => this.#stitchedController === controller && this.isConnected && dialog.open &&
      !dialog.classList.contains('dialog-closing') && this.returnFocusTarget === openerButton;
    const form = this.querySelector('product-form-component');
    const previousBusy = form?.getAttribute('aria-busy');
    const buttons = [...this.querySelectorAll('button[type="submit"]')].map(button => [button, button.disabled]);
    // Existing form submission also checks aria-busy, including keyboard submits.
    form?.setAttribute('aria-busy', 'true');
    for (const [button] of buttons) button.disabled = true;
    const color = this.querySelector('select[data-stitched-color]');
    const currentColor = color?.querySelector('option:not([data-stitched-product-url])');
    if (currentColor) color.value = currentColor.value;
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      if (previousBusy == null) form?.removeAttribute('aria-busy');
      else form?.setAttribute('aria-busy', previousBusy);
      for (const [button, disabled] of buttons) if (button.isConnected) button.disabled = disabled;
    };
    controller.signal.addEventListener('abort', cleanup, { once: true });
    let timedOut = false;
    const timer = setTimeout(() => {timedOut = true; controller.abort();}, 12000);
    try {
      const response = await fetch(url.href, { signal: controller.signal, cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) throw new Error('Color unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = html.querySelector('[data-product-grid-content]');
      if (grid?.querySelector('product-form-component')?.dataset.productId !== String(productId)) throw new Error('Unexpected color product');
      if (!active() || controller.signal.aborted) return;
      const title = html.querySelector('h1')?.textContent.trim() || '';
      await opener.updateQuickAddModal(grid, url.href, { title, replace: true });
      this.querySelector('select[data-stitched-color]')?.focus({ preventScroll: true });
    } catch {
      // Match ordinary quick-shop recovery, but never navigate after a close or a newer selection.
      if (active() && (!controller.signal.aborted || timedOut)) window.location.assign(url.href);
    } finally {
      clearTimeout(timer);
      cleanup();
      if (this.#stitchedController === controller) this.#stitchedController = undefined;
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.cancelStitchedSelection();
    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    const url = productUrlWithContext(anchorElement.href, this.dataset.productUrl);
    if (viewMoreDetailsLink) viewMoreDetailsLink.href = url;
    if (mobileProductTitle) mobileProductTitle.href = url;
  };

  #handleDialogClose = () => {
    this.cancelStitchedSelection();
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
